import { Dispatch, SetStateAction } from 'react';
import IUser from '~/Models/DTO/User.model';
import {
  setLocalStorageAccessToken,
  setLocalStorageProviderUUId,
  setLocalStorageRefreshToken,
} from '~/Hooks';
import { AuthModel } from '~/Models/DTO/Auth.model';
import { REQUEST_ACTIONS } from '../../../Constants/Actions.constants';
import { PROVIDER_SERVICE_URLS, USER_SERVICE_URLS } from '../../../Constants/Urls.constants';
import { Segment } from '../../../Helpers/Segment.helper';
import { IRequestWithCallBack } from '../../../Hooks/useRequest';
import { IErrorResponse } from '../../../Models/DTO/Error.models';
import { Provider } from '../../../Models/DTO/Provider.model';
import { IProviderCreation } from '../models/providerCreation.models';
import { IStudioName } from '../models/studioName.models';
import { IUserCreation } from '../models/userCreation.models';
import { IVerifyEmail } from '../models/verifyEmail.model';
import { IVerifyToken } from '../models/verifyToken.model';
import { IOrganisation } from '~/Models/DTO/Organisation.model';

export const checkStudioName = (
  request: IRequestWithCallBack,
  studioName: string,
  setData: Dispatch<SetStateAction<IStudioName>>,
  setStudioNameContainsAHyperLink: Dispatch<SetStateAction<boolean>>,
) => {
  request<IStudioName>(
    {
      url: PROVIDER_SERVICE_URLS.VERIFY_STUDIO_NAME,
      query: { studioName },
    },
    (data) => {
      setData(data);
      setStudioNameContainsAHyperLink(false);
    },
    () => setStudioNameContainsAHyperLink(true),
  );
};

export const verifyEmail = (
  request: IRequestWithCallBack,
  email: string,
  setData: (verifyEmail: IVerifyEmail) => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
) => {
  request<IVerifyEmail>(
    {
      url: USER_SERVICE_URLS.VERIFY_EMAIL,
      query: { email },
    },
    (data) => setData(data),
    (error) => setError(error),
  );
};

export const verifyAccountWithToken = (
  request: IRequestWithCallBack,
  token: string,
  setData: (verifyAccountWithToken: IVerifyToken) => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
) => {
  request<IVerifyToken>(
    {
      url: USER_SERVICE_URLS.VERIFY_ACCOUNT_TOKEN,
      query: { token },
      method: 'POST',
    },
    async (data) => {
      setData(data);
    },
    (error) => setError(error),
  );
};

export const resendEmailVerification = (
  request: IRequestWithCallBack,
  userUuid: string,
  setData: () => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
) => {
  request<IVerifyToken>(
    {
      url: USER_SERVICE_URLS.RESEND_EMAIL_VERIFICATION,
      query: { userUuid },
      method: 'POST',
      headers: { 'Client-Id': 'neo' },
    },
    () => setData(),
    (error) => setError(error),
  );
};

export const createANewUser = (
  request: IRequestWithCallBack,
  data: IUserCreation,
  callback: () => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
) => {
  request<AuthModel>(
    {
      actionName: REQUEST_ACTIONS.SIGNUP,
      url: USER_SERVICE_URLS.REGISTER,
      method: 'POST',
      // we need to provide header to let BE knows if user is coming from neo or pro
      headers: { 'Client-Id': 'neo' },
      data,
    },
    (data) => {
      Segment.track('account created', { category: 'Conversion' });
      setLocalStorageAccessToken(data.access_token);
      setLocalStorageRefreshToken(data.refresh_token);
      callback();
    },
    (error) => setError(error),
  );
};

export const createANewProvider = (
  request: IRequestWithCallBack,
  data: Partial<IProviderCreation>,
  setData: (providerCreation: { provider: Provider }) => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
  access_token?: string,
) => {
  request<{ provider: Provider }>(
    {
      actionName: REQUEST_ACTIONS.GET_PROVIDER,
      url: PROVIDER_SERVICE_URLS.CREATE_PROVIDER,
      data,
      method: 'POST',
      customToken: access_token || undefined,
    },
    (data) => {
      setLocalStorageProviderUUId(data.provider.uuid);
      setData(data);
    },
    (error) => setError(error),
  );
};

export const createANewOrganisation = (
  request: IRequestWithCallBack,
  data: Partial<IOrganisation>,
  callback: (data: Partial<IOrganisation>) => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
) =>
  request<AuthModel>(
    {
      actionName: REQUEST_ACTIONS.CREATE_ORGANISATION,
      url: PROVIDER_SERVICE_URLS.ORGANISATION_CREATE,
      method: 'POST',
      headers: { 'Client-Id': 'neo' },
      data,
    },
    (data: any) => callback(data),
    (error: any) => setError(error),
  );

export const upgradeCustomerToAProviderAccount = (
  request: IRequestWithCallBack,
  setData: (user: IUser) => void,
  setError: Dispatch<SetStateAction<IErrorResponse>>,
  access_token?: string,
) => {
  request<IUser>(
    {
      actionName: REQUEST_ACTIONS.GET_USER,
      url: USER_SERVICE_URLS.USER,
      data: JSON.stringify([{ op: 'replace', path: '/user/utype', value: 'pro' }]),
      method: 'PATCH',
      customToken: access_token || undefined,
    },
    (data) => setData(data),
    (error) => setError(error),
  );
};
